import type { ChangeEvent } from 'react'
import type { MatchingOffers, SingleMatch } from 'types/match'

import copy from 'copy.json'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import Button from 'components/Button'
import Select from 'components/Select'
import Spinner from 'components/Spinner'
import Status from 'components/Status'
import ReservationsHistoryModal from 'modals/ReservationsHistoryModal'
import StatusHistoryModal from 'modals/StatusHistoryModal'
import { useStore } from 'store'
import { reservationDays } from 'utils'

export type ReservationType = {
  value?: number
  date?: string
}

const texts = copy.page.matches.offer_status
const { button } = copy.common

type Props = {
  currentStatus: MatchingOffers['status']
  statusHistory: MatchingOffers['status_history']
  reservationsHistory: MatchingOffers['reservations_history']
  currentReservationLength: number
  statusOnly?: boolean
  loading?: boolean
  onStatusChange: () => void
}

export default function OfferStatus({
  currentStatus,
  statusHistory,
  reservationsHistory,
  currentReservationLength,
  statusOnly,
  loading,
  onStatusChange
}: Props) {
  const { api } = useStore()
  const { id } = useParams()
  const [statusHistoryModalOpen, setStatusHistoryModalOpen] = useState(false)
  const [reservationsHistoryModalOpen, setReservationsHistoryModalOpen] = useState(false)

  const handleStatusHistoryClick = () => {
    setStatusHistoryModalOpen(true)
  }

  const handleReservationHistoryClick = () => {
    setReservationsHistoryModalOpen(true)
  }

  const handleMatchAccepted = () => {
    if (id) {
      api.changeMatchingState(Number(id), 'accept', 'Akceptuj ofertę').then(onStatusChange)
    }
  }

  const handleMatchReservation = (reservationDays: number) => {
    if (id) {
      api
        .changeMatchingState(Number(id), 'reserve', 'Zarezerwuj ofertę', reservationDays)
        .then(onStatusChange)
    }
  }

  const handleMatchUnreserve = () => {
    if (id) {
      api
        .changeMatchingState(Number(id), 'uno-reserve', 'Rezygnacja z rezerwacji')
        .then(onStatusChange)
    }
  }

  const handleMatchUnAccept = () => {
    if (id) {
      api.changeMatchingState(Number(id), 'uno-accept', 'Zrezygnuj z oferty').then(onStatusChange)
    }
  }

  const handleMatchFinalize = () => {
    if (id) {
      api.changeMatchingState(Number(id), 'finalize', 'Finalizacja oferty').then(onStatusChange)
    }
  }

  return (
    <>
      <Wrapper>
        <h3>{texts.heading}</h3>

        <Content>
          {loading && (
            <LoadingOverlay>
              <Spinner size={40} />
            </LoadingOverlay>
          )}

          {currentStatus === 'ACTIVE' && (
            <ActiveOffer
              statusType="ACTIVE"
              onStatusHistoryClick={handleStatusHistoryClick}
              onReservationHistoryClick={handleReservationHistoryClick}
              onMatchAccept={handleMatchAccepted}
              onMatchReserve={handleMatchReservation}
              statusOnly={statusOnly}
            />
          )}

          {currentStatus === 'RESERVED' && (
            <ReservedOffer
              statusType="RESERVED"
              onStatusHistoryClick={handleStatusHistoryClick}
              onReservationHistoryClick={handleReservationHistoryClick}
              onMatchUnreserve={handleMatchUnreserve}
              onMatchAccept={handleMatchAccepted}
              onMatchReserve={handleMatchReservation}
              currentReservationLength={currentReservationLength}
              statusOnly={statusOnly}
            />
          )}

          {currentStatus === 'ACCEPTED' && (
            <AcceptedOffer
              statusType="ACCEPTED"
              onStatusHistoryClick={handleStatusHistoryClick}
              onMatchUnAccept={handleMatchUnAccept}
              onMatchFinalize={handleMatchFinalize}
              statusOnly={statusOnly}
            />
          )}

          {currentStatus === 'BLOCKED' && (
            <NoActionOffer
              statusType="BLOCKED"
              onStatusHistoryClick={handleStatusHistoryClick}
              statusOnly={statusOnly}
            />
          )}

          {currentStatus === 'FINALIZED' && (
            <NoActionOffer
              statusType="FINALIZED"
              onStatusHistoryClick={handleStatusHistoryClick}
              statusOnly={statusOnly}
            />
          )}

          {currentStatus === 'DELETED' && (
            <NoActionOffer
              statusType="DELETED"
              onStatusHistoryClick={handleStatusHistoryClick}
              statusOnly={statusOnly}
            />
          )}
        </Content>
      </Wrapper>

      <StatusHistoryModal
        statusHistory={statusHistory}
        isModalOpen={statusHistoryModalOpen}
        onModalClose={() => setStatusHistoryModalOpen(false)}
        closeButtonLabel={button.close}
      />

      <ReservationsHistoryModal
        reservationsHistory={reservationsHistory}
        isModalOpen={reservationsHistoryModalOpen}
        onModalClose={() => setReservationsHistoryModalOpen(false)}
        closeButtonLabel={button.close}
      />
    </>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Content = styled.div`
  display: flex;
  padding: 16px;
  gap: 20px;
  background: var(--very-light-blue);
  position: relative;

  > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;

    > div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }
  }

  h5 {
    font: var(--600-16);
  }

  ${Select} {
    flex: 1;
  }
`

const LoadingOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.5);
`

type StatusProp = {
  onStatusHistoryClick: () => void
  statusType: SingleMatch['status']
  statusOnly?: boolean
}

type ActiveOfferProps = {
  onMatchAccept: () => void
  onMatchReserve: (reservationDays: number) => void
  onReservationHistoryClick: () => void
} & StatusProp

export function ActiveOffer({
  statusType,
  statusOnly,
  onStatusHistoryClick,
  onReservationHistoryClick,
  onMatchAccept,
  onMatchReserve
}: ActiveOfferProps) {
  const [selectedReservationDays, setSelectedReservationDays] = useState('1')
  const reservations = reservationDays(7, 0)

  const handleReservationDayChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedReservationDays(event.currentTarget.value)
  }

  if (statusOnly) return <Status statusType={statusType} style={{ font: 'var(--600-16)' }} />

  return (
    <>
      <div>
        <div>
          <Status statusType={statusType} style={{ font: 'var(--600-16)' }} />
          <button className="green text" onClick={onStatusHistoryClick}>
            {texts.history_status}
          </button>
        </div>
        <Button secondary onClick={onMatchAccept}>
          {texts.in_progress}
        </Button>
      </div>

      <div>
        <div>
          <h5>{texts.reserve}</h5>
          <button className="green text" onClick={onReservationHistoryClick}>
            {texts.history_reservation}
          </button>
        </div>
        <div>
          <Select defaultValue={selectedReservationDays} onChange={handleReservationDayChange}>
            {reservations.map(({ value, date }) => (
              <option value={value} key={value}>
                {date}
              </option>
            ))}
          </Select>
          <Button onClick={() => onMatchReserve(Number(selectedReservationDays))}>
            {texts.reserve}
          </Button>
        </div>
      </div>
    </>
  )
}

type ReservedOfferType = {
  onMatchUnreserve: () => void
  onMatchAccept: () => void
  onMatchReserve: (reservationDays: number) => void
  onReservationHistoryClick: () => void
  currentReservationLength: number
} & StatusProp

export function ReservedOffer({
  statusType,
  statusOnly,
  onStatusHistoryClick,
  onReservationHistoryClick,
  onMatchUnreserve,
  onMatchAccept,
  onMatchReserve,
  currentReservationLength
}: ReservedOfferType) {
  const [selectedReservationDays, setSelectedReservationDays] = useState('1')
  const reservations = reservationDays(7, currentReservationLength)

  useEffect(() => {
    if (reservations.length === 1) {
      setSelectedReservationDays('1')
    }
  }, [reservations])

  const handleReservationDayChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedReservationDays(event.currentTarget.value)
  }

  const today = new Date()
  const dataToDisplay = new Date(today)
  dataToDisplay.setDate(today.getDate() + currentReservationLength)

  if (statusOnly) return <Status statusType={statusType} style={{ font: 'var(--600-16)' }} />

  return (
    <>
      <div>
        <Status statusType={statusType} style={{ font: 'var(--600-16)' }} />
        <div>
          <span>{`${texts.until} ${dataToDisplay.getDate()}.${
            dataToDisplay.getMonth() + 1
          }.${dataToDisplay.getFullYear()}`}</span>
          <button className="orange text" onClick={onMatchUnreserve}>
            {texts.cancel_reservation}
          </button>
        </div>
        <button className="green text" onClick={onStatusHistoryClick}>
          {texts.history_status}
        </button>
        <Button secondary onClick={onMatchAccept}>
          {texts.in_progress}
        </Button>
      </div>

      <div>
        <div>
          <h5>{texts.extend_reservation}</h5>
          <button className="green text" onClick={onReservationHistoryClick}>
            {texts.history_reservation}
          </button>
        </div>
        <div>
          {currentReservationLength >= 7 ? (
            <span>{texts.reservation_limit}</span>
          ) : (
            <>
              <Select defaultValue={selectedReservationDays} onChange={handleReservationDayChange}>
                {reservations.map(({ value, date }) => (
                  <option value={value} key={value}>
                    {date}
                  </option>
                ))}
              </Select>
              <Button onClick={() => onMatchReserve(Number(selectedReservationDays))}>
                {texts.extend}
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  )
}

type AcceptedOfferType = {
  onMatchUnAccept: () => void
  onMatchFinalize: () => void
} & StatusProp

export function AcceptedOffer({
  statusType,
  statusOnly,
  onStatusHistoryClick,
  onMatchUnAccept,
  onMatchFinalize
}: AcceptedOfferType) {
  if (statusOnly) return <Status statusType={statusType} style={{ font: 'var(--600-16)' }} />

  return (
    <>
      <div>
        <Status statusType={statusType} style={{ font: 'var(--600-16)' }} />
        <button className="green text" onClick={onStatusHistoryClick}>
          {texts.history_status}
        </button>
        <Button secondary onClick={onMatchUnAccept}>
          Zrezygnuj
        </Button>
      </div>

      <div>
        <Button onClick={onMatchFinalize}>{texts.finalize}</Button>
      </div>
    </>
  )
}

export function NoActionOffer({ statusType, statusOnly, onStatusHistoryClick }: StatusProp) {
  return (
    <div>
      <Status statusType={statusType} style={{ font: 'var(--600-16)' }} />

      {!statusOnly && (
        <button className="green text" onClick={onStatusHistoryClick}>
          {texts.history_status}
        </button>
      )}
    </div>
  )
}
